/**
 * API Client for Minora Backend
 * Single entry point for all API requests
 */

import { env } from "./env";
import { userFacingMessage } from "./user-facing-message";

interface FetchOptions extends RequestInit {
	params?: Record<string, string>;
}

/**
 * A failed response. Carries the status and the backend's `error` slug so
 * callers can branch on the specific failure instead of matching on message
 * text.
 */
export class ApiError extends Error {
	readonly status: number;
	readonly code?: string;

	constructor(message: string, status: number, code?: string) {
		super(userFacingMessage(message));
		this.name = "ApiError";
		this.status = status;
		this.code = code;
	}
}

/** The ads agent owns the chat until its run finishes. */
export function isAgentBusy(error: unknown): error is ApiError {
	return error instanceof ApiError && error.code === "agent_busy";
}

/** The chat refuses messages while a deletion waits for its Delete or Cancel button. */
export function isDeletionBlocked(error: unknown): error is ApiError {
	return error instanceof ApiError && error.code === "deletion_pending";
}

/** The answer was for a question that is no longer waiting, or the page did not know which one. */
export function isStaleAnswer(error: unknown): error is ApiError {
	return error instanceof ApiError &&
		(error.code === "question_not_pending" || error.code === "reload_required");
}

class ApiClient {
	// What the signed-in user may open; `features` is worked out on the server.
	async getMyAccess() {
		return this.request<import("@/lib/access").MyAccess>(
			"/api/me/access",
			{ credentials: "include" },
		);
	}
	// Manager page (Super Admin only; the API refuses everyone else).
	async getAdminUsers() {
		return this.request<import("@/lib/access").AdminUser[]>("/api/admin/users", {
			credentials: "include",
		});
	}
	async getAdminTeams() {
		return this.request<{ id: string; name: string }[]>("/api/admin/teams", {
			credentials: "include",
		});
	}
	/** A new team; the picked user becomes its Owner. */
	async createAdminTeam(name: string, ownerId: string) {
		return this.request<{ id: string; name: string }>("/api/admin/teams", {
			method: "POST",
			credentials: "include",
			body: JSON.stringify({ name, ownerId }),
		});
	}
	async createAdminUser(input: import("@/lib/access").NewAdminUser) {
		return this.request<{ id: string }>("/api/admin/new-user", {
			method: "POST",
			credentials: "include",
			body: JSON.stringify(input),
		});
	}
	async updateAdminUser(
		userId: string,
		changes: Partial<
			Pick<import("@/lib/access").AdminUser, "siteRole" | "fullAccess" | "featureAccess" | "isActive">
		>,
	) {
		return this.request<{ success: boolean }>(`/api/admin/users/${encodeURIComponent(userId)}`, {
			method: "PATCH",
			credentials: "include",
			body: JSON.stringify(changes),
		});
	}
	async addAdminUserToTeam(
		userId: string,
		place: { teamId: string; role: import("@/lib/access").AssignableTeamRole; fullAccess: boolean; featureAccess: import("@/lib/access").Feature[] },
	) {
		return this.request<{ success: boolean }>(`/api/admin/users/${encodeURIComponent(userId)}/teams`, {
			method: "POST",
			credentials: "include",
			body: JSON.stringify(place),
		});
	}
	/** Role in a team, access in that team, or both. */
	async updateAdminUserTeam(
		userId: string,
		teamId: string,
		changes: { role?: import("@/lib/access").AssignableTeamRole; fullAccess?: boolean; featureAccess?: import("@/lib/access").Feature[] },
	) {
		return this.request<{ success: boolean }>(
			`/api/admin/users/${encodeURIComponent(userId)}/teams/${encodeURIComponent(teamId)}`,
			{ method: "PATCH", credentials: "include", body: JSON.stringify(changes) },
		);
	}
	async removeAdminUserFromTeam(userId: string, teamId: string) {
		return this.request<{ success: boolean }>(
			`/api/admin/users/${encodeURIComponent(userId)}/teams/${encodeURIComponent(teamId)}`,
			{ method: "DELETE", credentials: "include" },
		);
	}
	async getNotifications(cursor?: string) {
		return this.request<{
			items: import("@/hooks/use-notifications").MonitoringNotification[];
			unread: number;
			nextCursor: string | null;
		}>(
			`/api/notifications${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
			{ credentials: "include" },
		);
	}
	async getNotification(id: string) {
		return this.request<
			import("@/hooks/use-notifications").MonitoringNotification
		>(`/api/notifications/${encodeURIComponent(id)}`, {
			credentials: "include",
		});
	}
	async readNotification(id: string) {
		return this.request<{ ok: boolean }>(`/api/notifications/${id}/read`, {
			method: "POST",
			credentials: "include",
		});
	}
	// A human click, from the notification panel or the chat. The server records
	// which; the model never approves on anyone's behalf.
	/** Delete or Cancel on a deletion the ads agent asked to confirm (M-05). */
	async answerDeletion(projectId: string, questionId: string, choice: "delete" | "cancel") {
		return this.request<{ status: "confirmed" | "cancelled" }>(
			`/api/projects/chat/${projectId}/deletion`,
			{
				method: "POST",
				credentials: "include",
				body: JSON.stringify({ questionId, choice }),
			},
		);
	}

	/** What a generated creative's card shows: its state, the agreed copy, and who may accept it. */
	async getCreative(creativeId: string) {
		return this.request<CreativeView>(`/api/creatives/${encodeURIComponent(creativeId)}`, {
			credentials: "include",
		});
	}

	/** A button on a creative's card. The work goes on after the answer; its outcome is posted in the chat. */
	async decideCreative(creativeId: string, body: { action: CreativeAction; comment?: string; taskId?: string }) {
		return this.request<{ status: string }>(`/api/creatives/${encodeURIComponent(creativeId)}/actions`, {
			method: "POST",
			credentials: "include",
			body: JSON.stringify(body),
		});
	}

	/** "Check again" on a change Meta never confirmed: one more read of Meta, answered when it is done. */
	async recheckMonitoringExecution(id: string) {
		return this.request<{
			result: "applied" | "not_applied" | "unreachable" | "settled" | "pending";
			reason?: "no_answer" | "access_denied";
		}>(`/api/meta-ads-monitoring/executions/${id}/recheck`, {
			method: "POST",
			credentials: "include",
		});
	}

	async answerMonitoringReview(
		id: string,
		action: "approve" | "reject",
		channel: "notification" | "chat" = "notification",
	) {
		return this.request<{
			status: "resolved" | "already_resolved" | "needs_clarification";
			reason?: string;
		}>(`/api/meta-ads-monitoring/reviews/${id}/answer`, {
			method: "POST",
			credentials: "include",
			body: JSON.stringify({ action, channel }),
		});
	}
	async getMonitoringReview(id: string) {
		return this.request<MonitoringReviewDetails>(
			`/api/meta-ads-monitoring/reviews/${id}`,
			{ credentials: "include" },
		);
	}
	private baseUrl: string;

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl;
	}

	private async request<T>(
		endpoint: string,
		options: FetchOptions = {},
	): Promise<T> {
		const { params, ...fetchOptions } = options;

		let url = `${this.baseUrl}${endpoint}`;

		if (params) {
			const searchParams = new URLSearchParams(params);
			url += `?${searchParams.toString()}`;
		}

		const response = await fetch(url, {
			...fetchOptions,
			headers: {
				"Content-Type": "application/json",
				...fetchOptions.headers,
			},
		});

		if (!response.ok) {
			const error = await response
				.json()
				.catch(() => ({ error: "Unknown error" }));
			throw new ApiError(
				error.message || error.error || "API request failed",
				response.status,
				typeof error.error === "string" ? error.error : undefined,
			);
		}

		return response.json();
	}

	async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
		return this.request<T>(endpoint, { params });
	}

	async post<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: "POST",
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	async patch<T>(endpoint: string, data: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: "PATCH",
			body: JSON.stringify(data),
		});
	}

	async delete<T>(
		endpoint: string,
		params?: Record<string, string>,
	): Promise<T> {
		return this.request<T>(endpoint, { method: "DELETE", params });
	}

	// ==================== Meta Ads ====================
	// Kicks off the Meta (Facebook) OAuth flow. Returns the dialog URL the
	// browser should navigate to so the user can grant access.
	async connectMetaAds(teamId?: string | null) {
		return this.request<{ url: string }>("/api/meta_ads/connect", {
			params: teamId ? { teamId } : undefined,
			credentials: "include",
		});
	}

	// Whether a Meta Ads account is connected for the current context. Scoped to
	// the active team when there is one, otherwise to the user personally.
	async getMetaAdsStatus(teamId?: string | null) {
		return this.request<MetaAdsStatus>("/api/meta_ads/status", {
			params: teamId ? { teamId } : undefined,
			credentials: "include",
		});
	}

	// Everything the Meta Ads agent can use as a parameter: pages, Instagram
	// accounts and pixels come from the sync, the rest the user adds by hand.
	async getMetaAdsAssets(teamId?: string | null) {
		return this.request<MetaAdsAssets>("/api/meta_ads/assets", {
			params: teamId ? { teamId } : undefined,
			credentials: "include",
		});
	}

	async saveMetaAdsAsset(
		tool: ManualAssetTool,
		config: Record<string, unknown>,
		teamId?: string | null,
	) {
		return this.request<{ rowId: string; tool: ManualAssetTool }>(
			`/api/meta_ads/assets/${tool}`,
			{
				method: "PUT",
				body: JSON.stringify(teamId ? { ...config, teamId } : config),
				credentials: "include",
			},
		);
	}

	// Every ad account this user reached through the Meta connection. The token
	// itself never leaves the server — hasToken only says whether the account can
	// be used at all.
	async getMetaAdsAdAccounts(teamId?: string | null) {
		return this.request<{ adAccounts: MetaAdsAdAccount[] }>(
			"/api/meta_ads/ad-accounts",
			{
				params: teamId ? { teamId } : undefined,
				credentials: "include",
			},
		);
	}

	// Which ad account the team (or the user personally) works against by default.
	async setDefaultMetaAdAccount(dbId: string, teamId?: string | null) {
		return this.request<{ ok: true; dbId: string }>(
			`/api/meta_ads/ad-accounts/${dbId}/default`,
			{
				method: "PUT",
				body: JSON.stringify(teamId ? { teamId } : {}),
				credentials: "include",
			},
		);
	}

	// Removes the Meta connection of the current context. Monitors on its ad
	// accounts are suspended until someone connects again.
	async disconnectMetaAds(teamId?: string | null) {
		return this.request<{ detached: number; suspended: number }>(
			"/api/meta_ads/connection",
			{
				method: "DELETE",
				params: teamId ? { teamId } : undefined,
				credentials: "include",
			},
		);
	}

	// Uploads one creative to a Meta ad account's media library.
	//
	// Not through `request`: that always sets Content-Type to application/json,
	// and a multipart body needs the browser to set it so the boundary comes with
	// it. Meta decides on resolution, duration and codec, so its rejection is
	// passed through as the error message rather than reworded.
	async uploadMetaAdsMedia(data: {
		file: File;
		adAccount?: string;
		teamId?: string | null;
	}) {
		const formData = new FormData();
		formData.append("file", data.file);
		if (data.adAccount) formData.append("adAccount", data.adAccount);
		if (data.teamId) formData.append("teamId", data.teamId);

		// A network-level failure ("failed to fetch") carries no status and no
		// body — most often the server dropped an oversized request at the
		// socket. Turn it into something a person can act on.
		// The scope goes in the URL too: the server checks access before it reads the file.
		const scope = new URLSearchParams();
		if (data.teamId) scope.set("teamId", data.teamId);
		if (data.adAccount) scope.set("adAccount", data.adAccount);

		let response: Response;
		try {
			response = await fetch(`${this.baseUrl}/api/meta_ads/media?${scope}`, {
				method: "POST",
				body: formData,
				credentials: "include",
			});
		} catch {
			throw new ApiError(
				"The upload never reached the server — the file may be too large, or the connection dropped.",
				0,
				"network_error",
			);
		}

		const body = await response.json().catch(() => ({}));

		if (!response.ok) {
			throw new ApiError(
				body.message || body.error || "Upload failed",
				response.status,
				typeof body.error === "string" ? body.error : undefined,
			);
		}

		return body as MetaAdsUploadedMedia;
	}

	// The ad account's media library as Meta reports it right now. Opening the
	// picker also reconciles: stored rows for media deleted on Meta's side go.
	async getMetaAdsLiveMedia(adAccount?: string, teamId?: string | null) {
		return this.request<MetaAdsLiveMedia>("/api/meta_ads/media/live", {
			params: {
				...(adAccount ? { adAccount } : {}),
				...(teamId ? { teamId } : {}),
			},
			credentials: "include",
		});
	}

	// Downloads the picked live files into the user's library. Partial success
	// is normal: the response is per-item, never a request-level failure.
	async importMetaAdsMedia(data: {
		mediaIds: string[];
		adAccount?: string;
		teamId?: string | null;
	}) {
		return this.request<MetaAdsImportResponse>("/api/meta_ads/media/import", {
			method: "POST",
			body: JSON.stringify({
				mediaIds: data.mediaIds,
				...(data.adAccount ? { adAccount: data.adAccount } : {}),
				...(data.teamId ? { teamId: data.teamId } : {}),
			}),
			credentials: "include",
		});
	}

	// ==================== Files (library) ====================
	// The backend for these two is still being written. The signatures are the
	// agreed contract; flip STUB_FILES_API off once /api/files lands and the
	// page needs no changes.
	private static readonly STUB_FILES_API = false;

	async getLibraryFiles(
		teamId?: string | null,
		filters?: { page: number; type: string; source: string },
	): Promise<{ files: LibraryFile[]; page?: number; hasMore?: boolean }> {
		if (ApiClient.STUB_FILES_API) return { files: [] };
		return this.request<{
			files: LibraryFile[];
			page: number;
			hasMore: boolean;
		}>("/api/files", {
			params: {
				...(teamId ? { teamId } : {}),
				...(filters
					? {
							page: String(filters.page),
							type: filters.type,
							source: filters.source,
						}
					: {}),
			},
			credentials: "include",
		});
	}

	async uploadLibraryFile(
		file: File,
		teamId?: string | null,
	): Promise<LibraryFile> {
		if (ApiClient.STUB_FILES_API) {
			throw new ApiError(
				"File uploads are not available yet.",
				501,
				"not_implemented",
			);
		}

		const formData = new FormData();
		formData.append("file", file);
		if (teamId) formData.append("teamId", teamId);

		// The team goes in the URL too: the server checks access before it reads the file.
		const scope = teamId ? `?${new URLSearchParams({ teamId })}` : "";

		let response: Response;
		try {
			response = await fetch(`${this.baseUrl}/api/files/upload${scope}`, {
				method: "POST",
				body: formData,
				credentials: "include",
			});
		} catch {
			throw new ApiError(
				"The upload never reached the server — the file may be too large, or the connection dropped.",
				0,
				"network_error",
			);
		}

		const body = await response.json().catch(() => ({}));
		if (!response.ok) {
			throw new ApiError(
				body.message || body.error || "Upload failed",
				response.status,
				typeof body.error === "string" ? body.error : undefined,
			);
		}
		return body as LibraryFile;
	}

	// Owner-only edits of a file's metadata: rename it, or set where it came
	// from (for rows older than the source column).
	async updateLibraryFile(
		id: string,
		data: { title?: string; source?: Exclude<FileSource, null> },
	) {
		return this.request<LibraryFile>(`/api/files/${id}`, {
			method: "PATCH",
			body: JSON.stringify(data),
			credentials: "include",
		});
	}

	async getFile(id: string) {
		return this.request<StoredFile>(`/api/files/${encodeURIComponent(id)}`, {
			credentials: "include",
		});
	}

	async getFileContent(id: string) {
		return this.request<StoredFile & { content: string }>(
			`/api/files/${encodeURIComponent(id)}/content`,
			{ credentials: "include" },
		);
	}

	fileDownloadUrl(id: string): string {
		return `${this.baseUrl}/api/files/${encodeURIComponent(id)}/download`;
	}
	filePreviewUrl(id: string): string {
		return `${this.baseUrl}/api/files/${encodeURIComponent(id)}/preview`;
	}

	async deleteMetaAdsAsset(tool: ManualAssetTool, rowId: string) {
		return this.request<{ deleted: boolean; rowId: string }>(
			`/api/meta_ads/assets/${tool}/${rowId}`,
			{
				method: "DELETE",
				credentials: "include",
			},
		);
	}

	// ==================== Teams ====================
	async getTeams() {
		return this.request<Team[]>("/api/teams", { credentials: "include" });
	}

	async getActiveTeam() {
		return this.request<{ team: Team | null }>("/api/teams/get-active", {
			credentials: "include",
		});
	}

	async setActiveTeam(teamId: string | null) {
		return this.request<{ success: boolean }>("/api/teams/set-active", {
			method: "POST",
			body: JSON.stringify({ teamId }),
			credentials: "include",
		});
	}

	async getTeam(teamId: string) {
		return this.request<
			Team & { currentUserRole: string; currentUserPermissions: string[] }
		>(`/api/teams/${teamId}`, { credentials: "include" });
	}

	async getTeamMembers(teamId: string) {
		return this.request<TeamMember[]>(`/api/teams/${teamId}/members`, {
			credentials: "include",
		});
	}

	async createTeam(data: {
		name: string;
		slug?: string;
		description?: string;
		planType?: string;
	}) {
		return this.request<Team>("/api/teams", {
			method: "POST",
			body: JSON.stringify(data),
			credentials: "include",
		});
	}

	async inviteToTeam(teamId: string, email: string, role: string) {
		return this.request<{ success: boolean }>(`/api/teams/${teamId}/invite`, {
			method: "POST",
			body: JSON.stringify({ email, role }),
			credentials: "include",
		});
	}

	async updateMemberRole(teamId: string, memberId: string, role: string) {
		return this.request<{ success: boolean }>(
			`/api/teams/${teamId}/members/${memberId}`,
			{
				method: "PATCH",
				body: JSON.stringify({ role }),
				credentials: "include",
			},
		);
	}

	async removeMember(teamId: string, memberId: string) {
		return this.request<{ success: boolean }>(
			`/api/teams/${teamId}/members/${memberId}`,
			{
				method: "DELETE",
				credentials: "include",
			},
		);
	}

	// ==================== Feature Flags ====================
	async getFeatureFlags() {
		return this.request<FeatureFlag[]>("/api/feature-flags", {
			credentials: "include",
		});
	}

	async checkFeatureFlag(key: string) {
		return this.request<{ enabled: boolean }>(`/api/feature-flags/${key}`, {
			credentials: "include",
		});
	}

	// ==================== Website Builder ====================
	async getGeneratedSites() {
		return this.request<GeneratedWebsite[]>("/api/website-builder/sites", {
			credentials: "include",
		});
	}

	async getGeneratedSite(id: string) {
		return this.request<GeneratedWebsiteWithMessages>(
			`/api/website-builder/sites/${id}`,
			{
				credentials: "include",
			},
		);
	}

	async generateWebsite(prompt: string, model: string) {
		return this.request<GeneratedWebsiteWithMessages>(
			"/api/website-builder/generate",
			{
				method: "POST",
				body: JSON.stringify({ prompt, model }),
				credentials: "include",
			},
		);
	}

	async sendWebsiteFollowUp(siteId: string, prompt: string) {
		return this.request<{ ok: boolean }>(
			`/api/website-builder/sites/${siteId}/follow-up`,
			{
				method: "POST",
				body: JSON.stringify({ prompt }),
				credentials: "include",
			},
		);
	}

	// ==================== Projects & Briefs ====================
	async getProjects(data: { userId: string; teamId?: string }) {
		const params: Record<string, string> = { userId: data.userId };
		if (data.teamId) params.teamId = data.teamId;
		return this.request<Project[]>("/api/projects", {
			params,
			credentials: "include",
		});
	}

	async createProject(data: {
		userId: string;
		teamId?: string;
		projectName: string;
	}) {
		return this.request<{ ProjectName: string; chatId: string }>(
			"/api/projects",
			{
				method: "POST",
				body: JSON.stringify(data),
				credentials: "include",
			},
		);
	}

	async updateProjectTitle(projectId: string, data: { title: string }) {
		return this.request<{ status: number; message: string }>(
			`/api/projects/title/${projectId}`,
			{
				method: "POST",
				body: JSON.stringify(data),
				credentials: "include",
			},
		);
	}

	async deleteProject(projectId: string) {
		return this.request<{ success: boolean }>(`/api/projects/${projectId}`, {
			method: "DELETE",
			credentials: "include",
		});
	}

	async submitBrief(data: {
		userId: string;
		teamId?: string;
		briefType: "short" | "detailed";
		businessType: "b2b" | "b2c";
		brief: Record<string, unknown>;
		chatId?: string; // optional — backend will auto-generate if not provided
		language?: "en" | "ru" | "uz"; // UI language for report generation
	}) {
		return this.request<{ message: string; chatId: string }>(
			"/api/projects/brief",
			{
				method: "POST",
				body: JSON.stringify(data),
				credentials: "include",
			},
		);
	}

	async getProjectFiles(conversationId: string) {
		return this.request<ProjectResources>(
			`/api/projects/files/${conversationId}`,
			{
				credentials: "include",
			},
		);
	}

	async getProjectDetails(projectId: string) {
		return this.request<Project>(`/api/projects/details/${projectId}`, {
			credentials: "include",
		});
	}

	async getChatHistory(conversationId: string) {
		return this.request<ChatHistoryMessage[]>(
			`/api/projects/chat/${conversationId}`,
			{
				credentials: "include",
			},
		);
	}

	/**
	 * `projectId` is optional — omit it to start a brand-new project from
	 * this message. The backend always creates/confirms a project either
	 * way and returns its id as `chatId` (see routes/projects.ts's
	 * projectIdChecker usage in /chat).
	 */
	async sendChatMessage(
		projectId: string | undefined,
		userQuestion: string,
		fileIds?: string[],
		teamId?: string,
		monitoringReviewId?: string,
		monitoringNotificationId?: string,
		/** The ads agent's question this message answers. */
		questionId?: string,
	) {
		return this.request<{ message: string; data: string; chatId: string }>(
			"/api/projects/chat",
			{
				method: "POST",
				body: JSON.stringify({
					project_id: projectId,
					teamId,
					userQuestion,
					fileIds: fileIds,
					monitoringReviewId,
					monitoringNotificationId,
					questionId,
				}),
				credentials: "include",
			},
		);
	}

	async getToolResults(conversationId: string) {
		return this.request<ToolResult[]>(
			`/api/projects/toolResults/${conversationId}`,
			{
				credentials: "include",
			},
		);
	}

	async getProjectBrief(conversationId: string) {
		return this.request<ProjectBrief>(`/api/projects/brief/${conversationId}`, {
			credentials: "include",
		});
	}

	async runCanvasTool(
		toolSlug: string,
		chatId: string,
		language?: "en" | "ru" | "uz",
		platform?: string,
	) {
		const body: { chatId: string; language: string; platform?: string } = {
			chatId,
			language: language ?? "ru",
		};
		if (platform) {
			body.platform = platform;
		}
		return this.request<{ message: string }>(`/api/projects/tool/${toolSlug}`, {
			method: "POST",
			body: JSON.stringify(body),
			credentials: "include",
		});
	}

	async uploadFile(data: {
		file: File;
		userId: string;
		conversationId: string;
		teamId?: string;
	}) {
		const formData = new FormData();
		formData.append("file", data.file);
		formData.append("userId", data.userId);
		formData.append("conversationId", data.conversationId);
		if (data.teamId) {
			formData.append("teamId", data.teamId);
		}

		// The project goes in the URL too: the server checks access before it reads the file.
		const scope = new URLSearchParams({ conversationId: data.conversationId });
		const response = await fetch(`${this.baseUrl}/api/projects/file/?${scope}`, {
			method: "POST",
			body: formData,
			credentials: "include",
		});

		if (!response.ok) {
			const error = await response
				.json()
				.catch(() => ({ error: "Unknown error" }));
			throw new Error(error.message || error.error || "Upload failed");
		}

		return response.json() as Promise<{ success: boolean; file: ProjectFile }>;
	}

	async generatePresentation(chatId: string, language: "en" | "ru" = "ru") {
		return this.request<{ success: boolean; message: string }>(
			"/api/projects/presentation/generate",
			{
				method: "POST",
				body: JSON.stringify({ chatId, language }),
				credentials: "include",
			},
		);
	}

	async getProjectFileSignedUrl(chatId: string, fileId: string) {
		return this.request<{ signedUrl: string }>("/api/projects/file/signedUrl", {
			params: { chatId, fileId },
			credentials: "include",
		});
	}

	// ==================== Canvas Comments ====================
	async getComments(conversationId: string) {
		return this.request<CanvasCommentRow[]>("/api/comments", {
			params: { conversationId },
			credentials: "include",
		});
	}

	async postComment(data: {
		conversationId: string;
		content: string;
		posX: number;
		posY: number;
	}) {
		return this.request<CanvasCommentRow>("/api/comments", {
			method: "POST",
			body: JSON.stringify(data),
			credentials: "include",
		});
	}

	async editComment(id: number, content: string) {
		return this.request<CanvasCommentRow>(`/api/comments/${id}`, {
			method: "PATCH",
			body: JSON.stringify({ content }),
			credentials: "include",
		});
	}

	async resolveComment(id: number) {
		return this.request<CanvasCommentRow>(`/api/comments/${id}/resolve`, {
			method: "PATCH",
			body: JSON.stringify({}),
			credentials: "include",
		});
	}

	// ==================== Site Analyzer (SEO) ====================
	async getAnalyses(teamId?: string) {
		return this.request<SeoAnalysis[]>("/api/site-analyzer", {
			params: teamId ? { teamId } : undefined,
			credentials: "include",
		});
	}

	async getAnalysisDetails(id: string, page = 0) {
		return this.request<SeoAnalysisDetailsResponse>(
			`/api/site-analyzer/${id}`,
			{ params: { page: String(page) }, credentials: "include" },
		);
	}

	async getAnalysisReports(id: string) {
		return this.request<{ reports: SeoFileReport[] }>(
			`/api/site-analyzer/${id}/reports`,
			{ credentials: "include" },
		);
	}

	async retryReportDelivery(analysisId: string, reportId: string) {
		return this.request<{ status: "pending" }>(
			`/api/site-analyzer/${analysisId}/reports/${reportId}/retry-delivery`,
			{
				method: "POST",
				credentials: "include",
			},
		);
	}

	async answerAnalysis(id: string, answer: string) {
		return this.request<{ status: "queued" }>(
			`/api/site-analyzer/${id}/answer`,
			{
				method: "POST",
				body: JSON.stringify({ answer }),
				credentials: "include",
			},
		);
	}

	async retryAnalysisResult(id: string) {
		return this.request<{ status: "queued" }>(
			`/api/site-analyzer/${id}/retry-result`,
			{
				method: "POST",
				credentials: "include",
			},
		);
	}

	async createAnalysis(data: {
		name: string;
		domain: string;
		teamId?: string;
		language?: "en" | "ru";
	}) {
		return this.request<SeoAnalysis>("/api/site-analyzer", {
			method: "POST",
			body: JSON.stringify({ ...data, engine: "hermes" }),
			credentials: "include",
		});
	}

	async renameAnalysis(id: string, name: string) {
		return this.request<SeoAnalysis>("/api/site-analyzer", {
			method: "PATCH",
			body: JSON.stringify({ id, name }),
			credentials: "include",
		});
	}

	async deleteAnalysis(id: string) {
		return this.request<{ success: boolean }>(`/api/site-analyzer/${id}`, {
			method: "DELETE",
			credentials: "include",
		});
	}

	async deleteAnalysisUrl(id: string) {
		return this.request<{ success: boolean }>(`/api/site-analyzer/url/${id}`, {
			method: "DELETE",
			credentials: "include",
		});
	}
}

// Project (Conversation) type
export interface Project {
	id: string;
	userId: string;
	teamId: string | null;
	title: string;
	createdAt: string;
	updatedAt: string;
}

// Canvas comment row — matches the DB schema from canvas_comments table
export interface CanvasCommentRow {
	id: number;
	conversationId: string;
	userId: string;
	userName?: string | null; // joined from user table
	content: string;
	posX: number;
	posY: number;
	resolved: boolean;
	createdAt: string;
}

export interface ProjectFile {
	id: string;
	title: string | null;
	mimeType: string | null;
	/** "generated" for a creative the creative agent made. */
	source?: string | null;
}

/**
 * A creative in one of the user's Meta ad accounts.
 *
 * `fileId` is set once our copy of the bytes has been stored; until then the
 * only preview is Meta's own signed url, which expires.
 */
export interface ProjectCreative {
	id: string;
	mediaId: string;
	mimeType: string;
	fileId: string | null;
	title: string;
	previewUrl: string | null;
}

export interface ProjectResources {
	files: ProjectFile[];
	creatives: ProjectCreative[];
}

export type ManualAssetTool =
	| "app"
	| "catalog"
	| "place_page_set"
	| "pixel"
	| "page"
	| "instagram";

export interface MetaAdsStatus {
	connected: boolean;
	/** Owner or manager of the team (always true in a personal workspace). */
	canConnect: boolean;
	/** Any member but a viewer. */
	canUse: boolean;
	connection: {
		connectedByUserId: string;
		connectedByName: string | null;
		createdAt: string;
	} | null;
	adAccounts: MetaAdsAdAccount[];
}

export interface MetaAdsAdAccount {
	dbId: string;
	adAccountId: string;
	name: string;
	isDefault: boolean;
	/** False when the connection lost its token — the account cannot be used. */
	hasToken: boolean;
}

/** What a person sees before approving a monitoring proposal. */
export interface MonitoringReviewDetails {
	id: string;
	status: "open" | "approved" | "rejected" | "obsolete" | string;
	action: string;
	level: string;
	metaId: string;
	reasonCode: string;
	severity: string;
	conversationId: string;
	/** Minor units (cents). The exact change applied after approval. */
	budget: { kind: string; before: string; after: string } | null;
	currency: string | null;
	metrics: Record<string, string | null>;
	/** When the statistics behind the proposal were downloaded from Meta. */
	collectedAt: string | null;
	createdAt: string;
	resolvedAt: string | null;
}

export interface MetaAdsUploadedMedia {
	mediaId: string;
	mediaType: "image" | "video";
	mimeType: string;
	name: string;
	/** Our stored copy, null if saving it to the bucket failed. */
	fileId: string | null;
	url: string | null;
	adAccount: { dbId: string; adAccountId: string };
}

export interface MetaAdsLiveMediaItem {
	mediaId: string;
	mediaType: "image" | "video";
	name: string;
	previewUrl: string | null;
	/** Already in the user's library — the picker disables it. */
	imported: boolean;
}

export interface MetaAdsLiveMedia {
	adAccount: { dbId: string; adAccountId: string };
	media: MetaAdsLiveMediaItem[];
	/** Which Meta listings answered. False = temporarily unavailable, not empty. */
	complete: { image: boolean; video: boolean };
	removed: number;
}

export interface MetaAdsImportResult {
	mediaId: string;
	ok: boolean;
	alreadyImported?: boolean;
	error?: string;
	media?: {
		dbId: string;
		mediaId: string;
		mimeType: string;
		name: string;
		previewUrl: string | null;
		fileId: string | null;
	};
}

export interface MetaAdsImportResponse {
	results: MetaAdsImportResult[];
	imported: number;
	skipped: number;
	failed: number;
}

export type FileSource = "uploaded" | "generated" | "meta" | null;

export interface LibraryFile {
	id: string;
	title: string | null;
	mimeType: string | null;
	source: FileSource;
	createdAt: string;
	sizeBytes?: number | null;
	report?: {
		id: string;
		analysisId: string;
		domain: string;
		filename: string;
		reportStatus: "complete" | "partial";
		deliveryState: string;
		createdAt: string;
	} | null;
}

export interface StoredFile extends LibraryFile {
	canPreview: boolean;
}

export interface SeoFileReport {
	id: string;
	analysisId: string;
	summaryMarkdown: string;
	coverage: Record<string, unknown>;
	limitations: string[];
	validationScope: string;
	requestedScope: Record<string, unknown> | null;
	measuredScope: Record<string, unknown> | null;
	reportStatus: "complete" | "partial";
	deliveryState: "pending" | "saving" | "ready" | "failed";
	attempts: number;
	errorStage: string | null;
	errorCode: string | null;
	lastError: string | null;
	createdAt: string;
	deliveredAt: string | null;
	files: Array<{
		filename: string;
		fileId: string | null;
		contentType: string;
		sizeBytes: number;
		deliveryState: string;
	}>;
}

export interface MetaAdsPageAsset {
	rowId: string;
	name: string;
	accountId: string;
	platform: string;
	parentAccountId: string | null;
}

export interface MetaAdsConfigRow<T> {
	rowId: string;
	config: T;
}

export interface MetaAdsAssets {
	adAccount: { dbId: string; adAccountId: string };
	pages: MetaAdsPageAsset[];
	instagram: MetaAdsPageAsset[];
	pixels: MetaAdsConfigRow<{ id: string; name?: string }>[];
	apps: MetaAdsConfigRow<{
		application_id: string;
		name: string;
		object_store_url: string;
		platform: "ios" | "android";
	}>[];
	catalogs: MetaAdsConfigRow<{
		product_catalog_id: string;
		name: string;
		product_sets: { id: string; name: string }[];
	}>[];
	placePageSets: MetaAdsConfigRow<{
		place_page_set_id: string;
		name: string;
	}>[];
}

export type CreativeAction = "accept" | "edit" | "reject" | "generate-new" | "no" | "continue" | "stop";

/** GET /api/creatives/:id: the state of one generated creative (one ad set). */
export interface CreativeView {
	creativeId: string;
	/** The current version; a card for an older one is out of date. */
	taskId: string | null;
	status: "generating" | "waiting" | "accepting" | "accepted" | "expired" | "closed";
	waitingFor: "result" | "new" | "change" | "continue" | null;
	decideBy: string | null;
	mediaType: "image" | "video";
	adsetName: string | null;
	fileId: string | null;
	round: number;
	aiGenerated: boolean;
	adCopy: { message: string; headline: string | null; cta: string | null; link: string | null };
	canAccept: boolean;
	/** Who may accept, by name, for a viewer who cannot. */
	acceptors: string[];
	acceptError: string | null;
	/** Stop was pressed; the generation has not ended yet. */
	stopping: boolean;
}

export interface ChatHistoryMessage {
	id: string;
	conversationId: string;
	parentId: string | null;
	role: "user" | "assistant";
	content: string | null;
	toolCall: unknown;
	toolCallId: string | null;
	tokenUsed: number | null;
	createdAt: string;
}

export interface ToolResult {
	id: string;
	conversationId: string;
	parentId: string | null;
	role: "system";
	content: string | null;
	toolCall: unknown;
	toolCallId: string | null;
	tokenUsed: number | null;
	createdAt: string;
}

/** Marker-row `tool` values, for rows written before descriptors existed. */
const LEGACY_TOOL_LABELS: Record<string, string> = {
	"campaign-strategy": "Стратегия кампании",
	"Deep Research": "Deep Research",
};

/**
 * Card title for one canvas result, in three falling-back steps: the
 * descriptor the writer attached to this row; failing that, the paired marker
 * row's tool name (rows saved before descriptors); failing that, the old
 * positional "Result N".
 */
export function canvasCardLabel(
	row: ToolResult,
	all: ToolResult[],
	index: number,
): string {
	const own = row.toolCall as { label?: unknown } | null;
	if (own && typeof own.label === "string" && own.label.trim()) {
		return own.label;
	}

	if (row.toolCallId) {
		const marker = all.find((candidate) => {
			const call = candidate.toolCall as {
				id?: unknown;
				tool?: unknown;
			} | null;
			return call?.id === row.toolCallId && typeof call?.tool === "string";
		});
		const tool = (marker?.toolCall as { tool?: string } | null)?.tool;
		if (tool) return LEGACY_TOOL_LABELS[tool] ?? tool;
	}

	return `Result ${index + 1}`;
}

export interface ProjectBrief {
	briefType: string;
	[key: string]: unknown;
}

// Team Types
export interface Team {
	id: string;
	name: string;
	slug: string;
	description: string | null;
	logoUrl: string | null;
	role: string;
	permissions: Record<string, boolean>;
	createdAt: string;
}

export interface TeamMember {
	id: string;
	name: string;
	email: string;
	image: string | null;
	role: string;
	permissions: Record<string, boolean>;
	joinedAt: string;
}

// Feature Flag Types
interface FeatureFlag {
	id: string;
	name: string;
	enabled: boolean;
	description?: string;
}

// Website Builder Types
export const WEBSITE_BUILDER_MODELS = [
	{
		id: "claude-opus-4-7",
		label: "Opus 4",
		description: "Best quality · Slower",
	},
	{
		id: "claude-sonnet-4-6",
		label: "Sonnet 4.6",
		description: "Balanced · Recommended",
	},
	{
		id: "claude-haiku-4-5-20251001",
		label: "Haiku 4.5",
		description: "Fastest · Cheapest",
	},
] as const;

export type WebsiteBuilderModel = (typeof WEBSITE_BUILDER_MODELS)[number]["id"];

export interface WebsiteBuilderMessage {
	id: string;
	siteId: string;
	role: "user" | "assistant";
	content: string;
	createdAt: string;
}

export interface GeneratedWebsite {
	id: string;
	userId: string;
	teamId: string | null;
	prompt: string;
	model: string;
	status: "queued" | "building" | "deployed" | "failed";
	containerName: string | null;
	deployedUrl: string | null;
	errorLog: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface GeneratedWebsiteWithMessages extends GeneratedWebsite {
	messages: WebsiteBuilderMessage[];
}

// ==================== Site Analyzer (SEO) Types ====================
export type SeoStatus =
	| "inProcess"
	| "completed"
	| "failed"
	| "needsInput"
	| "partial";

// One analysis run (seo_analysis_history row)
export interface SeoAnalysis {
	id: string;
	userId: string;
	teamId: string | null;
	name: string;
	domain: string;
	engine: "legacy" | "hermes";
	status: SeoStatus;
	error: unknown;
	createdAt: string;
	deleted: boolean;
}

// One analyzed URL (seo_analysis_details row).
// `report` is a JSON string of SeoAiReport (parse on the client — see parseAiReport).
// `techAnalysis` is the raw audit object.
export interface SeoAnalysisDetail {
	id: string;
	seoAnalysisId: string;
	techAnalysis: SeoTechReport | null;
	report: string | null;
	analyzedUrl: string;
	error: unknown;
	status: SeoStatus;
	createdAt: string;
	deleted: boolean;
}

export interface SeoAnalysisDetailsResponse {
	analysis: SeoAnalysis;
	details: SeoAnalysisDetail[];
	question: string | null;
	page: number;
	pageSize: number;
	hasMore: boolean;
}

// AI structured report — mirrors the backend ReportSchema (seo-agents.ts)
export type SeoCategoryKey =
	| "crawlability_indexability"
	| "on_page_seo"
	| "content_quality"
	| "technical_performance"
	| "mobile_page_experience"
	| "https_security"
	| "internal_linking"
	| "international_seo"
	| "structured_data"
	| "social_meta"
	| "ai_search_geo";

export interface SeoCategoryAssessment {
	status: "Issues found" | "Passed";
	findings: string;
	reference: string;
	recommendations: string;
}

export interface SeoPriorityIssue {
	category: SeoCategoryKey;
	issue: string;
}

export interface SeoAiReport {
	overall_assessment: string;
	detailed_findings: Partial<Record<SeoCategoryKey, SeoCategoryAssessment>>;
	priority_issues?: SeoPriorityIssue[];
}

// Raw tech audit — mirrors the backend SeoReportSchema (seo_analyzer.ts).
// Fields the backend types as z.any() are left loose here.
export interface SeoTechReport {
	ssl: unknown;
	robotsTxt: unknown;
	sitemap: unknown;
	performance: unknown;
	responsiveness: unknown;
	title: {
		content: string | null;
		exists: boolean;
		empty: boolean;
		length: number;
	};
	metaDescription: {
		content: string | null;
		exists: boolean;
		empty: boolean;
		length: number;
	};
	headings: { report: string[]; structure: { tag: string; text: string }[] };
	mediaAltText: { withAlt: number; withoutAlt: number; total: number };
	links: { checked: number; total: number; links: unknown[] };
	canonical: { exists?: boolean; href?: string | null; error?: string };
	hreflang: {
		exists?: boolean;
		tags?: { hreflang: string; href: string }[];
		error?: string;
	};
	multilingual: {
		isMultilingual: boolean;
		currentPageLanguage: string;
		message: string;
		links: unknown[];
	};
}

export const api = new ApiClient(env.API_URL);
